import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Building2, ChevronDown, EyeIcon, EyeOff, Key, LogOut, User, PenLine, Search, FilterX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LiveTimerIndicator from './LiveTimerIndicator';
import OfflineSyncBadge from './OfflineSyncBadge';
import NotificationBell from './NotificationBell';
import { MyDefaultSignatureCard } from './MyProfile';
import { resetAllSavedViews } from '../hooks/useSavedView';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getRoleBadgeVariant = (role) => {
  switch (role) {
    case 'admin': return 'destructive';
    case 'manager': return 'default';
    case 'employee': return 'secondary';
    default: return 'outline';
  }
};

const AppHeader = ({ user, logout, companyProfile, onOpenSearch }) => {
  const { t } = useTranslation();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [resetViewsOpen, setResetViewsOpen] = useState(false);
  const [resettingViews, setResettingViews] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) { toast.error(t('auto.misc.newPasswordsDoNotMatch')); return; }
    if (newPassword.length < 8) { toast.error(t('auto.misc.newPasswordMustBeAtLeast')); return; }
    setChangingPassword(true);
    try {
      await axios.put(`${API}/auth/change-password`, { current_password: currentPassword, new_password: newPassword });
      toast.success(t('auto.misc.passwordChangedSuccessfully'));
      setPasswordDialogOpen(false);
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setChangingPassword(false); }
  };

  const handleResetViews = async () => {
    setResettingViews(true);
    try {
      await resetAllSavedViews();
      toast.success(t('auto.misc.allYourSavedSearchesAndSorts'));
      setResetViewsOpen(false);
    } catch {
      toast.error(t('auto.misc.couldNotResetYourFiltersPlease'));
    } finally { setResettingViews(false); }
  };

  return (
    <>
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-3 min-h-16 py-2 sm:py-0 sm:h-16">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 w-full sm:w-auto sm:flex-1">
              <div className="h-8 sm:h-12 flex items-center justify-center shrink-0">
                {companyProfile?.header_logo_file ? (
                  <img src={`${BACKEND_URL}/api/company/header-logo/${companyProfile.header_logo_file}`} alt="Header Logo" className="h-8 sm:h-12 w-auto object-contain" />
                ) : companyProfile?.logo_file ? (
                  <img src={`${BACKEND_URL}/api/company/logo/${companyProfile.logo_file}`} alt="Company Logo" className="h-8 sm:h-12 w-auto object-contain" />
                ) : (
                  <div className="w-8 h-8 sm:w-12 sm:h-12 bg-primary rounded-lg flex items-center justify-center">
                    <Building2 className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                  </div>
                )}
              </div>
              <h1 className="text-sm sm:text-lg lg:text-xl font-bold text-slate-900 truncate leading-tight">
                <span className="hidden sm:inline">{companyProfile?.name || 'Expertfix Sp. z o.o.'} Project Management</span>
                <span className="sm:hidden">{companyProfile?.name || 'Expertfix'}</span>
              </h1>
            </div>
            <div className="flex items-center justify-end flex-wrap gap-1.5 sm:gap-4 w-full sm:w-auto sm:shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={onOpenSearch}
                className="h-9 w-9 p-0 shrink-0 text-slate-500 hover:text-slate-900 sm:h-9 sm:w-auto sm:px-3"
                title={t('auto.misc.searchEverythingCtrlK')}
                aria-label={t('auto.misc.searchEverything')}
                data-testid="global-search-trigger"
              >
                <Search className="w-4 h-4 sm:mr-2" />
                <span className="hidden lg:inline">{t('auto.misc.search')}</span>
                <kbd className="hidden lg:inline ml-3 rounded border bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-400">
                  {t('auto.misc.ctrlK')}
                </kbd>
              </Button>
              <OfflineSyncBadge />
              <LiveTimerIndicator user={user} />
              <NotificationBell />
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-slate-900">{user?.full_name}</p>
                <Badge variant={getRoleBadgeVariant(user?.role)} className="text-xs">{user?.role?.toUpperCase()}</Badge>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="user-menu-btn">
                    <User className="w-4 h-4 mr-2" />{t('auto.misc.account')}<ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setPasswordDialogOpen(true)} data-testid="change-password-menu-item">
                    <Key className="w-4 h-4 mr-2" />{t('auto.misc.changePassword')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSignatureDialogOpen(true)} data-testid="my-signature-menu-item">
                    <PenLine className="w-4 h-4 mr-2" />{t('auto.misc.mySignature')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setResetViewsOpen(true)} data-testid="reset-views-menu-item">
                    <FilterX className="w-4 h-4 mr-2" />{t('auto.misc.resetMyFilters')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="logout-menu-item">
                    <LogOut className="w-4 h-4 mr-2" />{t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <AlertDialog open={resetViewsOpen} onOpenChange={setResetViewsOpen}>
        <AlertDialogContent data-testid="reset-views-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('auto.misc.resetAllMyFilters')}</AlertDialogTitle>
            <AlertDialogDescription>
              Every list you've searched, filtered or re-sorted goes back to
              its defaults — the Projects, Customers and Employees lists plus
              the Tasks, Checklists, Test Reports, Damage, Documents and
              Surveys tabs. This only affects your own account; nobody else's
              view changes, and no records are deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-views-cancel">{t('auto.misc.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetViews}
              disabled={resettingViews}
              data-testid="reset-views-confirm"
            >
              {resettingViews ? t('auto.misc.resetting2') : t('auto.misc.resetMyFilters2')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Key className="w-5 h-5" />{t('auto.misc.changePassword')}</DialogTitle>
            <DialogDescription>{t('auto.misc.updateYourAccountPasswordYouWill')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="current-pwd">{t('auto.misc.currentPassword')}</Label>
              <div className="relative">
                <Input id="current-pwd" type={showCurrentPassword ? "text" : "password"} value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)} placeholder={t('auto.misc.enterYourCurrentPassword')} className="pr-10" data-testid="dialog-current-password" />
                <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-pwd">{t('auto.misc.newPassword')}</Label>
              <div className="relative">
                <Input id="new-pwd" type={showNewPassword ? "text" : "password"} value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)} placeholder={t('auto.misc.enterNewPassword')} className="pr-10" data-testid="dialog-new-password" />
                <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">{t('auto.misc.minimum8Characters')}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-pwd">{t('auto.misc.confirmNewPassword')}</Label>
              <Input id="confirm-pwd" type={showNewPassword ? "text" : "password"} value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)} placeholder={t('auto.misc.confirmNewPassword2')} data-testid="dialog-confirm-password" />
              {confirmPassword && newPassword !== confirmPassword && <p className="text-xs text-red-500">{t('auto.misc.passwordsDoNotMatch')}</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPasswordDialogOpen(false); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }} data-testid="dialog-cancel-btn">{t('auto.misc.cancel')}</Button>
            <Button onClick={handleChangePassword} disabled={changingPassword || !currentPassword || newPassword.length < 8 || newPassword !== confirmPassword}
              data-testid="dialog-change-password-submit">{changingPassword ? t('auto.misc.changing') : t('auto.misc.changePassword')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* My Signature dialog — quick access from the top-right
          Account menu so operators don't have to hunt for their
          profile to update their default signature. */}
      <Dialog open={signatureDialogOpen} onOpenChange={setSignatureDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PenLine className="w-5 h-5" />{t('auto.misc.myDefaultSignature')}</DialogTitle>
            <DialogDescription>
              {t('auto.misc.saveYourSignatureOnceAndRe')}
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <MyDefaultSignatureCard />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignatureDialogOpen(false)}>{t('auto.misc.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AppHeader;
